import { createApi } from '@reduxjs/toolkit/query/react'
import { generateRESTQuery, generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const translationsApi = createApi({
  reducerPath: 'translationsApi',
  refetchOnFocus: false,
  tagTypes: [
    'CampaignTranslations',
    'OfferTranslations',
    'CategoryTranslations',
    'ProviderTranslations',
    'SegmentTranslations',
    'PointsDistributionTranslations',
    'UpdateCardTranslations',
    'AnnoucementTranslations',
    'TenantTranslations',
    'ActionBoxTranslations'
  ],
  baseQuery: getBaseQuery(),
  endpoints: (builder) => ({
    setAutoTranslations: builder.mutation({
      query: (requestParams: AutoTranslationsParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'setAutoTranslations',
          requestParams
        )
    }),
    getCampaignTranslations: builder.query({
      query: (requestParams: GetCampaignTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getCampaignTranslations',
          requestParams
        ),
      providesTags: [{ type: 'CampaignTranslations' }]
    }),
    editCampaignTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editCampaignTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'CampaignTranslations' }]
    }),
    getOfferTranslations: builder.query({
      query: (requestParams: GetOfferTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getOfferTranslations',
          requestParams
        ),
      providesTags: [{ type: 'OfferTranslations' }]
    }),
    editOfferTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editOfferTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'OfferTranslations' }]
    }),
    getCategoryTranslations: builder.query({
      query: (requestParams: GetCategoryTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getCategoryTranslations',
          requestParams
        ),
      providesTags: [{ type: 'CategoryTranslations' }]
    }),
    editCategoryTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editCategoryTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'CategoryTranslations' }]
    }),
    getProviderTranslations: builder.query({
      query: (requestParams: GetProviderTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getProviderTranslations',
          requestParams
        ),
      providesTags: [{ type: 'ProviderTranslations' }]
    }),
    editProviderTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editProviderTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'ProviderTranslations' }]
    }),
    getSegmentTranslations: builder.query({
      query: (requestParams: GetSegmentTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getSegmentTranslations',
          requestParams
        ),
      providesTags: [{ type: 'SegmentTranslations' }]
    }),
    editSegmentTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editSegmentTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'SegmentTranslations' }]
    }),
    getPointsDistributionTranslations: builder.query({
      query: (requestParams: GetPointsDistributionTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getPointsDistributionTranslations',
          requestParams
        ),
      providesTags: [{ type: 'PointsDistributionTranslations' }]
    }),
    editPointsDistributionTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editPointsDistributionTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'PointsDistributionTranslations' }]
    }),
    validateTranslationFields: builder.mutation({
      query: (requestParams: TranslationsValidationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'validateTranslationFields',
          requestParams
        )
    }),
    getUpdateCardTranslations: builder.query({
      query: (requestParams: GetUpdateCardTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getUpdateCardTranslations',
          requestParams
        ),
      providesTags: [{ type: 'UpdateCardTranslations' }]
    }),
    editUpdateCardTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editUpdateCardTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'UpdateCardTranslations' }]
    }),
    getAnnouncementTranslations: builder.query({
      query: (requestParams: GetAnnouncementTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getNewFeatureDialogsTranslations',
          requestParams
        ),
      providesTags: [{ type: 'AnnoucementTranslations' }]
    }),
    editAnnouncementTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editNewFeatureDialogsTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'AnnoucementTranslations' }]
    }),
    getDefaultTenantConfig: builder.query({
      query: (requestParams: GetDefaultTenantParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getDefaultTenantConfig',
          requestParams
        )
    }),
    getTenantConfigTranslations: builder.query({
      query: (requestParams: GetTenantTranslationParams) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'getTenantConfigTranslations',
          requestParams
        ),
      providesTags: [{ type: 'TenantTranslations' }]
    }),
    editTenantConfigTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editTenantConfigTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'TenantTranslations' }]
    }),
    getActionBoxTranslations: builder.query<
      ActionBoxTranslationProps,
      GetActionBoxTranslationParams
    >({
      query: (queryParams: GetActionBoxTranslationParams) =>
        generateRESTQuery({
          path: `${apiConstants.ACTION_BOXES_REST_URL}/translations/${queryParams.ActionBoxID}`,
          queryParams: { TenantID: queryParams.TenantID }
        }),
      providesTags: [{ type: 'ActionBoxTranslations' }]
    }),
    editActionBoxTranslations: builder.mutation({
      query: (requestParams: UpdateTranslationsObject) =>
        generateRPCQuery(
          apiConstants.TRANSLATIONS_RPC_PATH,
          'editActionBoxTranslations',
          requestParams
        ),
      invalidatesTags: [{ type: 'ActionBoxTranslations' }]
    })
  })
})

export const {
  useSetAutoTranslationsMutation,
  useGetCampaignTranslationsQuery,
  useEditCampaignTranslationsMutation,
  useGetOfferTranslationsQuery,
  useEditOfferTranslationsMutation,
  useEditCategoryTranslationsMutation,
  useGetCategoryTranslationsQuery,
  useGetProviderTranslationsQuery,
  useEditProviderTranslationsMutation,
  useGetSegmentTranslationsQuery,
  useEditSegmentTranslationsMutation,
  useGetPointsDistributionTranslationsQuery,
  useEditPointsDistributionTranslationsMutation,
  useValidateTranslationFieldsMutation,
  useGetUpdateCardTranslationsQuery,
  useEditUpdateCardTranslationsMutation,
  useGetAnnouncementTranslationsQuery,
  useEditAnnouncementTranslationsMutation,
  useGetDefaultTenantConfigQuery,
  useGetTenantConfigTranslationsQuery,
  useEditTenantConfigTranslationsMutation,
  useGetActionBoxTranslationsQuery,
  useEditActionBoxTranslationsMutation
} = translationsApi
